import type { DashboardPageName, ThemeMode } from '../types'
import { useNavigate } from 'react-router-dom'

const PAGE_TITLES: Record<DashboardPageName, { main: string; sub: string }> = {
  dashboard:          { main: 'Dashboard', sub: 'Overview' },
  contacts:           { main: 'Contacts',  sub: 'All contacts' },
  lapsed:             { main: 'Lapsed',    sub: 'Needs attention' },
  engaging:           { main: 'Engaging',  sub: 'In conversation' },
  recovered:          { main: 'Recovered', sub: 'Recovered contacts' },
  messages:           { main: 'Messages',  sub: 'Inbox' },
  reports:            { main: 'Reports',   sub: 'This month' },
  business:           { main: 'Business',  sub: 'Business profile' },
  services:           { main: 'Offerings', sub: 'Offerings catalog' },
  settings:           { main: 'Settings',  sub: 'Preferences' },
  profile:            { main: 'Profile',   sub: 'Your account' },
  'admin-metrics':    { main: 'Admin',     sub: 'Nudge metrics' },
  'admin-slots':      { main: 'Admin',     sub: 'Scheduler slots' },
  'admin-billing':    { main: 'Admin',     sub: 'Subscriptions' },
  'admin-businesses': { main: 'Admin',     sub: 'Businesses' },
  'admin-ai-health':  { main: 'Admin',     sub: 'AI health' },
  'admin-overview':   { main: 'Admin',     sub: 'Overview' },
  'admin-users':      { main: 'Admin',     sub: 'Users' },
}

function timeOfDayGreeting(now: Date): string {
  const hour = now.getHours()
  if (hour < 5) return 'Still up'
  if (hour < 12) return 'Good morning'
  if (hour < 17) return 'Good afternoon'
  if (hour < 22) return 'Good evening'
  return 'Working late'
}

export function TopNav({
  theme,
  setTheme,
  activePage,
  totalContacts,
  lapsedContacts,
  userFirstName,
}: {
  theme: ThemeMode
  setTheme: (t: ThemeMode) => void
  activePage: DashboardPageName
  totalContacts: number
  lapsedContacts: number
  userFirstName?: string | null
}) {
  const navigate = useNavigate()
  const now = new Date()
  const todayLabel = now.toLocaleDateString('en-US', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })

  // On the Dashboard page, show a personalized greeting instead of the static
  // "Dashboard" title — feels like a real "home" surface on every visit.
  const baseTitles = {
    ...PAGE_TITLES,
    contacts: { main: 'Contacts', sub: `${totalContacts} contacts` },
    lapsed:   { main: 'Lapsed',   sub: `${lapsedContacts} lapsed` },
  }

  const titleEntry = baseTitles[activePage]
  const isHome = activePage === 'dashboard'
  const greeting = timeOfDayGreeting(now)
  const main = isHome && userFirstName
    ? `${greeting}, ${userFirstName}`
    : isHome
      ? `${greeting}`
      : titleEntry.main
  const sub = isHome ? `Here's what's happening today` : titleEntry.sub

  return (
    <div
      className="h-16 bg-white/80 dark:bg-[#0e0e0e]/80 backdrop-blur-sm border-b border-black/[0.06] dark:border-white/10 flex items-center px-7 gap-4 sticky top-0 z-[5] transition-colors duration-200"
      id="pageTitle"
    >
      {/* Page title */}
      <div className="flex-1 flex flex-col gap-1">
        <div
          className="text-[15px] font-semibold text-gray-900 dark:text-gray-100 leading-none tracking-tight"
          style={{ fontFamily: "'Manrope', sans-serif" }}
        >
          {main}
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-[5px] h-[5px] rounded-full bg-[#e05a2b] shrink-0" />
          <span className="text-[11px] font-mono text-gray-400 dark:text-gray-500 leading-none">{sub}</span>
        </div>
      </div>

      {/* Right actions */}
      <div className="flex items-center gap-2">
        {/* Date */}
        <span className="text-[11px] font-mono text-gray-400 dark:text-gray-500 mr-1.5">{todayLabel}</span>

        {/* Home button — neo-brutal landing pill DNA */}
        <button
          type="button"
          onClick={() => navigate('/')}
          title="Go to Home"
          className="inline-flex items-center gap-1.5 px-4 py-[6px] rounded-full text-[12.5px] font-bold text-gray-900 dark:text-gray-100 bg-white dark:bg-neutral-900 border border-black dark:border-white/40 shadow-[2px_2px_0_#111] dark:shadow-[2px_2px_0_rgba(255,255,255,0.25)] transition-all duration-150
            hover:bg-[#e05a2b] hover:text-white hover:border-black hover:-translate-x-[1px] hover:-translate-y-[1px] hover:shadow-[3px_3px_0_#111] dark:hover:shadow-[3px_3px_0_rgba(255,255,255,0.3)]
            active:translate-x-[1px] active:translate-y-[1px] active:shadow-[1px_1px_0_#111] dark:active:shadow-[1px_1px_0_rgba(255,255,255,0.2)]"
          style={{ fontFamily: "'Manrope', sans-serif" }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg>
          Home
        </button>

        {/* Theme toggle — neo-brutal landing pill DNA */}
        <button
          type="button"
          onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
          title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
          className="grid place-items-center w-[34px] h-[34px] rounded-full bg-white dark:bg-neutral-900 text-gray-900 dark:text-gray-100 border border-black dark:border-white/40 shadow-[2px_2px_0_#111] dark:shadow-[2px_2px_0_rgba(255,255,255,0.25)] transition-all duration-150
            hover:bg-[#1e1b4b] hover:text-white hover:border-black hover:-translate-x-[1px] hover:-translate-y-[1px] hover:shadow-[3px_3px_0_#111] dark:hover:shadow-[3px_3px_0_rgba(255,255,255,0.3)]
            active:translate-x-[1px] active:translate-y-[1px] active:shadow-[1px_1px_0_#111] dark:active:shadow-[1px_1px_0_rgba(255,255,255,0.2)]"
        >
          {theme === 'light' ? (
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
            </svg>
          ) : (
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <circle cx="12" cy="12" r="5" />
              <line x1="12" y1="2" x2="12" y2="4" /><line x1="12" y1="20" x2="12" y2="22" />
              <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
              <line x1="2" y1="12" x2="4" y2="12" /><line x1="20" y1="12" x2="22" y2="12" />
            </svg>
          )}
        </button>
      </div>

      <div id="topnavAction" />
    </div>
  )
}
